import { IconAlertCircleFilled, IconCaretDownFilled, IconChartBar, IconChartHistogram, IconChartLine, IconChevronLeft } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import classNames from 'classnames';
import {
    Nav,
    NavItem,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { NSCard, NSButton } from 'bricks';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { URLS } from 'services/urls';
import { TaskStatusResponse } from 'queries/InvestmentMemo/useTaskStatus';
import './InvestmentMemoSideNav.scss';
import assertNever from 'helpers/assertNever';
import { LatestInvestmentMemoResponse } from 'queries/InvestmentMemo/useLatestInvestmentMemo';
import { useInvestmentMemoContext } from '../InvestmentMemoContext';
import { INVESTMENT_MEMO_NAV, InvestmentMemoNavItem, EXPORT_FORMAT } from '../constants';
import { SelectedProFormaOption } from '../types';
import TaskStatusDisplay from './TaskStatusDisplay';

export interface IInvestmentMemoSideNavProps {
    activeNav: InvestmentMemoNavItem;
    navigateToSection: (sideNavName: InvestmentMemoNavItem) => void;
    disableGenerationButton: boolean;
    selectedProFormaOption: SelectedProFormaOption | undefined;
    onGenerateDeck: (exportFormat: 'pdf' | 'pptx') => void;
    isGenerating?: boolean;
    organizationId: string;
    isDeckPending: boolean;
    mergedTaskStatus?: TaskStatusResponse;
    latestInvestmentMemo?: LatestInvestmentMemoResponse;
}

const InvestmentMemoSideNav = ({
    activeNav,
    navigateToSection,
    selectedProFormaOption,
    disableGenerationButton,
    onGenerateDeck,
    isGenerating = false,
    organizationId,
    isDeckPending,
    mergedTaskStatus,
    latestInvestmentMemo,
}: IInvestmentMemoSideNavProps) => {
    const navigate = useNavigate();

    const { hasNavError } = useInvestmentMemoContext();

    const navItemClass = (tag: InvestmentMemoNavItem) => classNames('d-flex align-items-center px-4 py-2 cursor--pointer', {
        'bg-primary-lighten text-primary font-weight-bold': tag === activeNav,
        'text-muted': tag !== activeNav,
    });

    const getNavIcon = (navItem: InvestmentMemoNavItem) => {
        switch (navItem) {
            case InvestmentMemoNavItem.Summary:
                return IconChartLine;
            case InvestmentMemoNavItem.DealOverview:
                return IconChartBar;
            case InvestmentMemoNavItem.Financials:
                return IconChartHistogram;
            default:
                return assertNever(navItem);
        }
    };

    const handleNavItemClick = (navName: InvestmentMemoNavItem) => {
        navigateToSection(navName);
    };

    const handleExit = () => {
        navigate(URLS.DEALS.HOME);
    };

    return (
        <div className="InvestmentMemoSideNav-wrapper position-sticky">
            <NSCard className="NSCard--level-2 InvestmentMemoSideNav w-100 position-sticky d-flex flex-column">
                <div className="px-3 pt-3">
                    <NSButton
                        callback={handleExit}
                        color="primary"
                        className="font-13 px-2 mb-2"
                        size="sm"
                        outline
                        allowReadOnlyAccess
                    >
                        <NSIcon icon={IconChevronLeft} className="mr-1" size="xs" />
                        Exit
                    </NSButton>
                    <h4 className="text-dark mt-3 mb-0 font-weight-bold">{INVESTMENT_MEMO_NAV.TITLE}</h4>
                    <p className="mt-1 text-wrap">{INVESTMENT_MEMO_NAV.SUBTITLE}</p>
                </div>
                <hr className="mb-0" />

                <div className="InvestmentMemoSideNav__content d-flex flex-column">
                    <ReadOnlyWrapper isReadOnly={!selectedProFormaOption}>
                        <Nav tabs className="InvestmentMemoSideNav__nav flex-column w-100 border-0">
                            {Object.entries(INVESTMENT_MEMO_NAV.NAV_ITEMS).map(([key, navName]) => {
                                const navItem = key as InvestmentMemoNavItem;
                                return (
                                    <NavItem
                                        key={key}
                                        className={navItemClass(navItem)}
                                        onClick={() => handleNavItemClick(navItem)}
                                        data-toggle="tab"
                                        id={`nav-item-${key}`}
                                    >
                                        <NSIcon icon={getNavIcon(navItem)} className="mr-2" />
                                        <div className="pl-1 flex-grow-1" aria-hidden="true">
                                            <span>{navName}</span>
                                        </div>
                                        {hasNavError(navItem) && (
                                            <NSIcon
                                                icon={IconAlertCircleFilled}
                                                size="1x"
                                                style={{ fontSize: '0.9rem' }}
                                                className="text-danger"
                                                data-testid={`nav-error-${key}`}
                                            />
                                        )}
                                    </NavItem>
                                );
                            })}
                        </Nav>
                    </ReadOnlyWrapper>
                </div>

                <div className="InvestmentMemoSideNav__footer p-3">
                    <UncontrolledDropdown className="w-100">
                        <DropdownToggle
                            color="primary"
                            className="w-100 d-flex align-items-center justify-content-between"
                            disabled={!selectedProFormaOption || isGenerating || disableGenerationButton || isDeckPending}
                        >
                            <span>Generate deck</span>
                            <NSIcon icon={IconCaretDownFilled} size="sm" />
                        </DropdownToggle>
                        <DropdownMenu className="w-100">
                            <DropdownItem onClick={() => onGenerateDeck(EXPORT_FORMAT.PDF)}>
                                Export as PDF
                            </DropdownItem>
                            <DropdownItem onClick={() => onGenerateDeck(EXPORT_FORMAT.PPTX)}>
                                Export as PowerPoint
                            </DropdownItem>
                        </DropdownMenu>
                    </UncontrolledDropdown>

                    {selectedProFormaOption && mergedTaskStatus && (
                        <TaskStatusDisplay
                            organizationId={organizationId}
                            dealId={selectedProFormaOption.value.dealId}
                            proFormaId={selectedProFormaOption.value.id}
                            latestInvestmentMemo={latestInvestmentMemo}
                            taskStatus={mergedTaskStatus}
                        />
                    )}
                </div>
            </NSCard>
        </div>
    );
};

export default InvestmentMemoSideNav;
